import React, { useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { motion } from "framer-motion"
import { SiteHeader } from "../site-header"
import { ScrollReveal } from "../ui/scroll-reveal"
import { Button } from "../ui/button"
import { ArrowRight, CheckCircle } from "lucide-react"
import { EventCategory } from "../../lib/analytics"
import { safeTrackEvent } from "../../lib/analytics-utils"

interface Deliverable {
  title: string
  description: string
}

interface Benefit {
  title: string
  description: string
}

interface ProcessStep {
  title: string
  description: string
}

interface RelatedService {
  name: string
  description: string
  href: string
}

interface ServicePageLayoutProps {
  // Metadata
  title: string
  metaDescription: string

  // Hero
  headline: string
  tagline: string
  backgroundImage: string
  backgroundAlt?: string

  // Intro section (below hero)
  introContent: string[]

  // Sections
  deliverables: Deliverable[]
  benefits: Benefit[]
  audience: string[]

  // Optional process section
  processSteps?: ProcessStep[]
  processHeadline?: string

  // Related services
  relatedServices: RelatedService[]

  // CTA
  ctaHeadline: string
  ctaDescription: string
  ctaButtonText: string
}

export function ServicePageLayout({
  title,
  metaDescription,
  headline,
  tagline,
  backgroundImage,
  backgroundAlt = "Background image",
  introContent,
  deliverables,
  benefits,
  audience,
  processSteps,
  processHeadline = "How It Works",
  relatedServices,
  ctaHeadline,
  ctaDescription,
  ctaButtonText
}: ServicePageLayoutProps) {
  const location = useLocation()
  const pageName = location.pathname.replace(/^\/|\/$/g, '').replace(/\//g, '_')

  // Set metadata
  useEffect(() => {
    document.title = title

    let metaDescriptionTag = document.querySelector('meta[name="description"]')
    if (!metaDescriptionTag) {
      metaDescriptionTag = document.createElement('meta')
      metaDescriptionTag.setAttribute('name', 'description')
      document.head.appendChild(metaDescriptionTag)
    }
    metaDescriptionTag.setAttribute('content', metaDescription)
  }, [title, metaDescription])

  const handleCtaClick = () => {
    safeTrackEvent({
      category: EventCategory.Engagement,
      label: `engagement_${pageName}_schedule_consultation`,
      context: {
        sourcePage: location.pathname,
        destinationPage: '/contact',
        section: 'service_cta'
      }
    })
  }

  const handleRelatedServiceClick = (serviceName: string, href: string) => {
    safeTrackEvent({
      category: EventCategory.Navigation,
      label: `nav_${pageName}_related_${serviceName.toLowerCase().replace(/ /g, '_')}`,
      context: {
        sourcePage: location.pathname,
        destinationPage: href,
        section: 'related_services'
      }
    })
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 overflow-hidden min-h-[500px] flex items-center">
          <div className="absolute inset-0 z-0 bg-background">
            <div className="relative w-full h-full">
              <img
                src={backgroundImage}
                alt={backgroundAlt}
                className="object-cover hero-bg w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/70 to-background" />
            </div>
          </div>

          <div className="container relative z-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto text-center space-y-6 pt-8"
            >
              <h1
                className="text-2xl font-light tracking-tight sm:text-3xl md:text-4xl lg:text-5xl text-foreground/90 leading-tight"
                dangerouslySetInnerHTML={{ __html: headline }}
              />
              <p className="text-xl text-muted-foreground font-light">
                {tagline}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-16">
          <div className="container">
            <div className="max-w-3xl mx-auto space-y-6">
              {introContent.map((paragraph, index) => (
                <p key={index} className="text-lg text-muted-foreground leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* What You Get Section */}
        <section className="py-16">
          <div className="container">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-8">
                  What You Get
                </h2>
                <div className="space-y-6">
                  {deliverables.map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="flex-shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{item.title}</h3>
                        <p className="text-muted-foreground mt-1">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* How It Works Section (Optional) */}
        {processSteps && processSteps.length > 0 && (
          <section className="py-16 bg-primary/5">
            <div className="container">
              <ScrollReveal>
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-8">
                    {processHeadline}
                  </h2>
                  <div className="space-y-8">
                    {processSteps.map((step, index) => (
                      <div key={index} className="flex gap-6">
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                            {index + 1}
                          </div>
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-foreground">{step.title}</h3>
                          <p className="text-muted-foreground mt-2">{step.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </section>
        )}

        {/* Why This Matters Section */}
        <section className={processSteps ? "py-16" : "py-16 bg-primary/5"}>
          <div className="container">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-8">
                  Why This Matters
                </h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {benefits.map((benefit, index) => (
                    <div key={index} className="p-6 rounded-lg border bg-card">
                      <h3 className="font-semibold text-lg text-foreground mb-2">{benefit.title}</h3>
                      <p className="text-muted-foreground">{benefit.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Who This Is For Section */}
        <section className={processSteps ? "py-16 bg-primary/5" : "py-16"}>
          <div className="container">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-8">
                  Who This Is For
                </h2>
                <ul className="space-y-3">
                  {audience.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="py-16">
          <div className="container">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold tracking-tight sm:text-3xl mb-8">
                  Related Services
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {relatedServices.map((service, index) => (
                    <Link
                      key={index}
                      to={service.href}
                      onClick={() => handleRelatedServiceClick(service.name, service.href)}
                      className="group p-6 rounded-lg border bg-card hover:border-primary/50 hover:bg-primary/5 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                          {service.name}
                        </h3>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" />
                      </div>
                      <p className="text-sm text-muted-foreground mt-2">{service.description}</p>
                    </Link>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 pb-32">
          <div className="container">
            <ScrollReveal>
              <div className="max-w-3xl mx-auto text-center space-y-8 bg-primary/5 rounded-xl p-10 border border-primary/10">
                <h2 className="text-3xl font-bold">{ctaHeadline}</h2>
                <p className="text-xl text-muted-foreground">
                  {ctaDescription}
                </p>
                <div className="pt-4">
                  <Button
                    asChild
                    size="lg"
                    className="text-lg px-10"
                    onClick={handleCtaClick}
                  >
                    <Link to="/contact">{ctaButtonText}</Link>
                  </Button>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>
    </div>
  )
}
